/**
 * Оффлайн загрузка - IndexedDB и Service Worker интеграция
 * Автор: AI Market Hub
 * 
 * Функционал:
 * - Сохранение загрузок в IndexedDB при оффлайне
 * - Регистрация Service Worker
 * - Background Sync для автоматического возобновления
 */

const DB_NAME = 'upload-queue';
const DB_VERSION = 1;
const STORE_NAME = 'pending';

// ============= IndexedDB =============

/**
 * Открывает базу данных IndexedDB
 * @returns {Promise<IDBDatabase>}
 */
function openDatabase() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => {
      console.error('[OfflineUpload] Ошибка открытия IndexedDB:', request.error);
      reject(request.error);
    };
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('queuedAt', 'queuedAt', { unique: false });
        console.log('[OfflineUpload] IndexedDB store создан');
      }
    };
    
    request.onsuccess = (event) => {
      resolve(event.target.result);
    };
  });
}

/**
 * Сохраняет загрузку в очередь IndexedDB
 * @param {Object} uploadData - Данные загрузки
 * @param {string} uploadData.id - Уникальный ID
 * @param {string} uploadData.fileName - Имя файла
 * @param {number} uploadData.fileSize - Размер файла
 * @param {string} uploadData.userId - ID пользователя
 * @param {string} uploadData.tusUrl - URL TUS загрузки (для возобновления)
 * @param {number} uploadData.uploadedBytes - Загружено байт
 * @returns {Promise<void>}
 */
export async function queueOfflineUpload(uploadData) {
  try {
    const db = await openDatabase();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      const data = {
        ...uploadData,
        queuedAt: Date.now(),
        status: 'pending'
      };
      
      const request = store.put(data);
      
      request.onsuccess = () => {
        console.log(`[OfflineUpload] Загрузка ${uploadData.id} сохранена в очередь`);
        
        // Регистрируем Background Sync если доступен
        if ('serviceWorker' in navigator && 'sync' in window.registration) {
          navigator.serviceWorker.ready.then((registration) => {
            registration.sync.register('video-upload')
              .then(() => console.log('[OfflineUpload] Background Sync зарегистрирован'))
              .catch((err) => console.warn('[OfflineUpload] Background Sync недоступен:', err));
          });
        }
        
        resolve();
      };
      
      request.onerror = () => {
        console.error('[OfflineUpload] Ошибка сохранения:', request.error);
        reject(request.error);
      };
      
      transaction.oncomplete = () => db.close();
    });
  } catch (error) {
    console.error('[OfflineUpload] Ошибка queueOfflineUpload:', error);
    throw error;
  }
}

/**
 * Получает все незавершённые загрузки из очереди
 * @returns {Promise<Array>}
 */
export async function getPendingUploads() {
  try {
    const db = await openDatabase();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();
      
      request.onsuccess = () => {
        const uploads = request.result || [];
        console.log(`[OfflineUpload] Найдено ${uploads.length} незавершённых загрузок`);
        resolve(uploads);
      };
      
      request.onerror = () => {
        console.error('[OfflineUpload] Ошибка получения:', request.error);
        reject(request.error);
      };
      
      transaction.oncomplete = () => db.close();
    });
  } catch (error) {
    console.error('[OfflineUpload] Ошибка getPendingUploads:', error);
    return [];
  }
}

/**
 * Удаляет загрузку из очереди
 * @param {string} id - ID загрузки
 * @returns {Promise<void>}
 */
export async function removePendingUpload(id) {
  try {
    const db = await openDatabase();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(id);
      
      request.onsuccess = () => {
        console.log(`[OfflineUpload] Загрузка ${id} удалена из очереди`);
        resolve();
      };
      
      request.onerror = () => {
        console.error('[OfflineUpload] Ошибка удаления:', request.error);
        reject(request.error);
      };
      
      transaction.oncomplete = () => db.close();
    });
  } catch (error) {
    console.error('[OfflineUpload] Ошибка removePendingUpload:', error);
  }
}

/**
 * Обновляет статус загрузки в очереди
 * @param {string} id - ID загрузки
 * @param {string} status - Новый статус
 * @param {Object} updates - Дополнительные обновления
 * @returns {Promise<void>}
 */
export async function updatePendingUpload(id, status, updates = {}) {
  try {
    const db = await openDatabase();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const getRequest = store.get(id);
      
      getRequest.onsuccess = () => {
        const data = getRequest.result;
        if (!data) {
          resolve();
          return;
        }
        
        const updatedData = {
          ...data,
          ...updates,
          status,
          updatedAt: Date.now()
        };
        
        const putRequest = store.put(updatedData);
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error);
      };
      
      getRequest.onerror = () => reject(getRequest.error);
      transaction.oncomplete = () => db.close();
    });
  } catch (error) {
    console.error('[OfflineUpload] Ошибка updatePendingUpload:', error);
  }
}

/**
 * Очищает все загрузки из очереди
 * @returns {Promise<void>}
 */
export async function clearPendingUploads() {
  try {
    const db = await openDatabase();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();
      
      request.onsuccess = () => {
        console.log('[OfflineUpload] Очередь очищена');
        resolve();
      };
      
      request.onerror = () => reject(request.error);
      transaction.oncomplete = () => db.close();
    });
  } catch (error) {
    console.error('[OfflineUpload] Ошибка clearPendingUploads:', error);
  }
}

// ============= Service Worker =============

/**
 * Регистрирует Service Worker
 * @returns {Promise<ServiceWorkerRegistration|null>}
 */
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    console.warn('[OfflineUpload] Service Worker не поддерживается');
    return null;
  }
  
  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/'
    });
    
    console.log('[OfflineUpload] Service Worker зарегистрирован:', registration.scope);
    
    // Обработка обновлений
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      console.log('[OfflineUpload] Найдено обновление Service Worker');
      
      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          console.log('[OfflineUpload] Новая версия Service Worker готова');
          // Можно показать уведомление пользователю о необходимости обновить страницу
        }
      });
    });
    
    // Сохраняем registration глобально для Background Sync
    window.swRegistration = registration;
    
    return registration;
  } catch (error) {
    console.error('[OfflineUpload] Ошибка регистрации Service Worker:', error);
    return null;
  }
}

/**
 * Подписывается на сообщения от Service Worker
 * @param {Function} onMessage - Callback для обработки сообщений
 */
export function subscribeToServiceWorkerMessages(onMessage) {
  if (!('serviceWorker' in navigator)) return;
  
  navigator.serviceWorker.addEventListener('message', (event) => {
    console.log('[OfflineUpload] Сообщение от SW:', event.data);
    if (onMessage) {
      onMessage(event.data);
    }
  });
}

/**
 * Отправляет сообщение Service Worker
 * @param {Object} message - Сообщение
 */
export function sendMessageToServiceWorker(message) {
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
    console.warn('[OfflineUpload] Service Worker не активен');
    return;
  }
  
  navigator.serviceWorker.controller.postMessage(message);
}

// ============= Хуки для React =============

/**
 * Проверяет, находится ли приложение в оффлайне
 * @returns {boolean}
 */
export function isOffline() {
  return !navigator.onLine;
}

/**
 * Проверяет поддержку Background Sync
 * @returns {boolean}
 */
export function supportsBackgroundSync() {
  return 'serviceWorker' in navigator && 'SyncManager' in window;
}

/**
 * Проверяет поддержку IndexedDB
 * @returns {boolean}
 */
export function supportsIndexedDB() {
  return 'indexedDB' in window;
}

export { DB_NAME, STORE_NAME };

